export function digitalQuantityUpdates(items) {
  const kept = new Set();
  const updates = {};
  for (const item of items) {
    if (item.digital !== true) continue;
    const product = String(item.product_id);
    const quantity = kept.has(product) ? 0 : 1;
    kept.add(product);
    if (item.quantity !== quantity) updates[item.key] = quantity;
  }
  return updates;
}

export function createDigitalQuantityClient({ request, cartUrl, updateUrl, onAdjusted = () => {} }) {
  let pending = Promise.resolve();
  const enqueue = (operation) => {
    const next = pending.then(operation, operation);
    pending = next.catch(() => {});
    return next;
  };
  async function readState() {
    const response = await request(`${cartUrl}?view=do3d-quantity`, {
      credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' }
    });
    if (!response.ok) throw new Error('digital_quantity_unavailable');
    const state = await response.json();
    if (state.version !== 1 || !Array.isArray(state.items)) throw new Error('digital_quantity_unavailable');
    return state;
  }
  async function normalize(sections, sectionsUrl) {
    const state = await readState();
    const updates = digitalQuantityUpdates(state.items);
    if (!Object.keys(updates).length) return null;
    const response = await request(updateUrl, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, ...(sections ? { sections } : {}), ...(sectionsUrl ? { sections_url: sectionsUrl } : {}) })
    });
    const cart = await response.json();
    if (!response.ok || cart.status || cart.errors) throw new Error('digital_quantity_unavailable');
    if (Object.keys(digitalQuantityUpdates((await readState()).items)).length) {
      throw new Error('digital_quantity_unavailable');
    }
    onAdjusted(cart);
    return cart;
  }
  return {
    normalize: (sections) => enqueue(() => normalize(sections)),
    fetch: (url, options) => enqueue(async () => {
      const response = await request(url, options);
      if (!response.ok) return response;
      const original = await response.clone().json();
      if (original.status || original.errors) return response;
      const sections = original.sections ? Object.keys(original.sections) : undefined;
      let sectionsUrl;
      if (options?.body instanceof FormData) sectionsUrl = options.body.get('sections_url');
      else if (typeof options?.body === 'string') {
        try { sectionsUrl = JSON.parse(options.body).sections_url; } catch {}
      }
      const corrected = await normalize(sections, sectionsUrl);
      if (!corrected) return response;
      const isAdd = /\/cart\/add(?:\.js)?(?:\?|$)/.test(String(url));
      const result = isAdd ? { ...original, ...(corrected.sections ? { sections: corrected.sections } : {}) } : corrected;
      return new Response(JSON.stringify(result), {
        status: response.status, statusText: response.statusText,
        headers: { 'Content-Type': 'application/json' }
      });
    })
  };
}

let storefrontClient;
function getClient() {
  if (!storefrontClient) {
    const config = document.getElementById('do3d-digital-quantity-config');
    storefrontClient = createDigitalQuantityClient({
      request: globalThis.fetch.bind(globalThis),
      cartUrl: config.dataset.cartUrl,
      updateUrl: config.dataset.updateUrl,
      onAdjusted: () => showQuantityMessage(config.dataset.adjusted)
    });
  }
  return storefrontClient;
}

function showQuantityMessage(message) {
  let status = document.getElementById('do3d-digital-quantity-status');
  if (!status) {
    status = document.createElement('div');
    status.id = 'do3d-digital-quantity-status';
    status.setAttribute('role', 'status');
    status.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);z-index:2147483000;max-width:90vw;width:max-content;padding:14px 20px;border-radius:8px;background:#111;color:#fff;font-size:15px;box-shadow:0 4px 24px #0003';
    document.body.append(status);
  }
  status.textContent = message;
  status.hidden = false;
  clearTimeout(showQuantityMessage.timer);
  showQuantityMessage.timer = setTimeout(() => { status.hidden = true; }, 10000);
}

export async function digitalCartFetch(url, options) {
  try {
    return await getClient().fetch(url, options);
  } catch (error) {
    const message = document.getElementById('do3d-digital-quantity-config').dataset.error;
    showQuantityMessage(message);
    throw new Error(message, { cause: error });
  }
}

export async function ensureDigitalQuantity() {
  try {
    return await getClient().normalize();
  } catch (error) {
    const message = document.getElementById('do3d-digital-quantity-config').dataset.error;
    showQuantityMessage(message);
    throw new Error(message, { cause: error });
  }
}

if (typeof document !== 'undefined') {
  const initialConfig = document.getElementById('do3d-digital-quantity-config');
  if (initialConfig) {
    try {
      const initialState = JSON.parse(initialConfig.textContent);
      if (Object.keys(digitalQuantityUpdates(initialState.items)).length) {
        ensureDigitalQuantity().then((corrected) => {
          if (corrected) window.location.reload();
        }).catch(() => {});
      }
    } catch {
      showQuantityMessage(initialConfig.dataset.error);
    }
  }
  let checkingCheckout = false;
  const allowedSubmissions = new WeakSet();
  window.addEventListener('pageshow', () => { checkingCheckout = false; });
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (event.submitter?.name !== 'checkout') return;
    if (allowedSubmissions.has(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingCheckout) return;
    checkingCheckout = true;
    const originalFields = new FormData(form);
    const quantities = {};
    for (const component of document.querySelectorAll('cart-items-component')) {
      component.cancelPendingQuantityChange?.();
    }
    for (const input of Array.from(form.elements)) {
      if (input.name !== 'updates[]' && !input.name?.startsWith('updates[')) continue;
      const key = input.closest('[data-key]')?.dataset.key;
      if (key && Number.isInteger(Number(input.value)) && Number(input.value) >= 0) {
        quantities[key] = Number(input.value);
      }
    }
    try {
      if (Object.keys(quantities).length) {
        const saved = await digitalCartFetch(document.getElementById('do3d-digital-quantity-config').dataset.updateUrl, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: quantities })
        });
        const savedCart = await saved.json();
        if (!saved.ok || savedCart.status || savedCart.errors) throw new Error('digital_quantity_unavailable');
      }
      await ensureDigitalQuantity();
      const currentForm = (form.id && document.getElementById(form.id)) || form;
      const submitter = currentForm.querySelector('[name="checkout"]') || document.querySelector(`[form="${currentForm.id}"][name="checkout"]`) || event.submitter;
      const preserveFields = (formEvent) => {
        for (const name of new Set(formEvent.formData.keys())) {
          if (name === 'updates' || name.startsWith('updates[')) formEvent.formData.delete(name);
        }
        if (currentForm === form) return;
        for (const name of new Set(originalFields.keys())) {
          if (name === 'updates' || name.startsWith('updates[') || name === 'checkout') continue;
          formEvent.formData.delete(name);
          for (const value of originalFields.getAll(name)) formEvent.formData.append(name, value);
        }
      };
      currentForm.addEventListener('formdata', preserveFields, { once: true });
      allowedSubmissions.add(currentForm);
      try {
        currentForm.requestSubmit(submitter);
      } finally {
        allowedSubmissions.delete(currentForm);
        currentForm.removeEventListener('formdata', preserveFields);
      }
    } catch {
      showQuantityMessage(document.getElementById('do3d-digital-quantity-config').dataset.error);
    } finally {
      checkingCheckout = false;
    }
  }, true);
}
